import { FaBars, FaUser, FaChartBar, FaBuilding, FaFileAlt, FaClipboardList, FaHome, FaUsers, FaDatabase, FaSignOutAlt, FaTasks, FaShieldAlt, FaBrain, FaBullseye, FaCheckSquare, FaMapMarkedAlt, FaUserTie, FaCalendarAlt, FaCalendarDay, FaUserClock, FaGraduationCap, FaBriefcase, FaCog, FaHeartbeat, FaChevronDown } from "react-icons/fa";
import { Link , useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { apiService } from "../services/apiService";
import { API_ENDPOINTS } from "../config/api";
import { toast } from "react-hot-toast";
import "./dummy.css";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false); // Start closed by default
  const [hrMenuOpen, setHrMenuOpen] = useState(false);
  const userRole = localStorage.getItem("userRole");
  const nestHrMenu = userRole === "Super Admin" || userRole === "Manager";
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsNavOpen(false); // Always start closed on mobile
      } else {
        setIsNavOpen(true); // Open on desktop
      }
    };
    
    // Set initial state based on screen size
    handleResize();
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleNavToggle = () => {
    setIsNavOpen(!isNavOpen);
  };

  // Update layout class for content shifting
  useEffect(() => {
    const layoutElement = document.querySelector('.layout');
    if (layoutElement) {
      if (isNavOpen) {
        layoutElement.classList.add('sidebar-open');
        layoutElement.classList.remove('sidebar-closed');
      } else {
        layoutElement.classList.add('sidebar-closed');
        layoutElement.classList.remove('sidebar-open');
      }
    }
  }, [isNavOpen]);

  const hrAdmin = ["/hr", "/hr/employees", "/hr/attendance", "/hr/leave", "/hr/performance", "/hr/learning", "/hr/recruitment", "/hr/settings"];

  const roleBasedMenu = {
    "Lead-Employee": ["/profile", "/dashboard", "/leads", "/clients", "/properties", "/newopp" , "/pipeline", "/todos", "/tasks"],
    "Manager": ["/profile", "/dashboard", "/target-achievement", "/leads", "/clients", "/properties", "/newopp", "/pipeline", "/todos", "/tasks", "/updateopportunity", "/rrport", "/report-daily", ...hrAdmin],
    "BO-Client": ["/profile", "/dashboard", "/leads", "/clients", "/properties", "/newopp",  "/pipeline" ,"/todos", "/tasks", "/rrport", "/report-daily", "/updateopportunity"],
    "FE-Property": ["/profile", "/dashboard", "/clients", "/properties", "/newopp", "/todos", "/tasks"],
    "Super Admin": ["/profile", "/dashboard", "/target-achievement", "/leads", "/clients", "/properties", "/newopp", "/pipeline", "/todos", "/tasks", "/updateopportunity", "/rrport", "/report-daily", "/employee", "/audit-logs", ...hrAdmin ],
    "Product-Manager": ["/profile", "/dashboard", "/leads", "/clients", "/properties", "/newopp", "/pipeline", "/todos", "/tasks", "/updateopportunity",  "/rrport"],
    "MIC": ["/profile", "/dashboard", "/target-achievement", "/leads", "/clients", "/properties", "/newopp", "/pipeline", "/tasks", "/rrport", "/report-daily"],
    "HR": ["/profile", ...hrAdmin],
    "Employee": ["/profile"],
    "BO-Lead": ["/profile", "/dashboard"],
  };

  const menuItems = {
    "/profile": { name: "Profile", icon: <FaUser /> },
    "/dashboard": { name: "Dashboard", icon: <FaHome /> },
    "/target-achievement": { name: "Target vs Achievement", icon: <FaBullseye /> },
    "/leads": { name: "Leads", icon: <FaClipboardList /> },
    "/clients": { name: "Clients", icon: <FaUsers /> },
    "/properties": { name: "Properties", icon: <FaBuilding /> },
    "/newopp": { name: "Opportunities", icon: <FaChartBar /> },
    "/pipeline": { name: "Pipeline", icon: <FaChartBar /> },
    "/todos": { name: "To-Do", icon: <FaCheckSquare /> },
    "/tasks": { name: "Tasks", icon: <FaTasks /> },
    "/updateopportunity": { name: "Closed Opportunity", icon: <FaFileAlt /> },
    // "/data": { name: "Data", icon: <FaDatabase /> },
    "/rrport": { name: "Reports", icon: <FaFileAlt /> },
    "/report-daily": { name: "Daily Report", icon: <FaCalendarDay /> },
    // "/ai-insights": { name: "AI Insights", icon: <FaBrain /> },
    "/employee": { name: "Employee", icon: <FaUsers /> },
    "/cityadd": { name: "City & Area", icon: <FaMapMarkedAlt /> },
    "/audit-logs": { name: "Audit Logs", icon: <FaShieldAlt /> },
    "/hr": { name: "HR Dashboard", icon: <FaHeartbeat /> },
    "/hr/me": { name: "My HR", icon: <FaUserTie /> },
    "/hr/employees": { name: "HR Employees", icon: <FaUsers /> },
    "/hr/attendance": { name: "Attendance", icon: <FaUserClock /> },
    "/hr/leave": { name: "Leave", icon: <FaCalendarAlt /> },
    "/hr/performance": { name: "Performance", icon: <FaBullseye /> },
    "/hr/learning": { name: "Learning", icon: <FaGraduationCap /> },
    "/hr/recruitment": { name: "Hiring", icon: <FaBriefcase /> },
    "/hr/settings": { name: "HR Settings", icon: <FaCog /> },
  };

  // Check if current path is active
  const isActivePath = (path) => {
    if (location.pathname === path) return true;
    if (path !== "/hr" && path.startsWith("/hr") && location.pathname.startsWith(`${path}/`)) {
      return true;
    }
    return false;
  };

  const { crmPaths, hrPaths } = useMemo(() => {
    const all = roleBasedMenu[userRole] || [];
    if (!nestHrMenu) {
      return { crmPaths: all, hrPaths: [] };
    }
    return {
      crmPaths: all.filter((path) => path !== "/hr" && !path.startsWith("/hr/")),
      hrPaths: all.filter((path) => path === "/hr" || path.startsWith("/hr/")),
    };
  }, [userRole, nestHrMenu]);

  const hrSectionActive = hrPaths.some((path) => isActivePath(path) || location.pathname.startsWith("/hr"));

  useEffect(() => {
    if (hrSectionActive) setHrMenuOpen(true);
  }, [hrSectionActive]);

  const renderNavLink = (path) => {
    const menuItem = menuItems[path];
    const isActive = isActivePath(path);
    return (
      <li key={path}>
        <Link
          to={path}
          className={isActive ? "active" : ""}
          data-tooltip={menuItem?.name || path.substring(1)}
          aria-label={menuItem?.name || path.substring(1)}
        >
          {menuItem?.icon || <FaFileAlt />}
          {isNavOpen && <span>{menuItem?.name || path.substring(1)}</span>}
        </Link>
      </li>
    );
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Call logout API
      await apiService.post(API_ENDPOINTS.auth.logout);
      
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('columnVisibilityClient');
      localStorage.removeItem('columnVisibilityProperty');
      
      // Show success message
      toast.success('Logged out successfully');
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      // Even if logout fails on server, clear local data
      console.error('Logout error:', error);
      
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('columnVisibilityClient');
      localStorage.removeItem('columnVisibilityProperty');
      
      // Redirect to login
      navigate('/login');
    }
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${isNavOpen ? "open" : "closed"}`}>
        <button 
          onClick={handleNavToggle} 
          className="toggle-btn"
          aria-label={isNavOpen ? "Close sidebar" : "Open sidebar"}
        >
          <FaBars />
        </button>

        <nav>
          <ul>
            {crmPaths.map(renderNavLink)}
            {hrPaths.length > 0 && (
              <li className={`sidebar-hr-group ${hrMenuOpen && isNavOpen ? "open" : ""}`}>
                <button
                  type="button"
                  className={`sidebar-hr-toggle ${hrSectionActive ? "active" : ""}`}
                  data-tooltip="HR"
                  aria-label="HR"
                  aria-expanded={hrMenuOpen}
                  onClick={() => {
                    if (!isNavOpen) {
                      navigate(hrPaths[0]);
                      return;
                    }
                    setHrMenuOpen((open) => !open);
                  }}
                >
                  <FaHeartbeat />
                  {isNavOpen && <span>HR</span>}
                  {isNavOpen && (
                    <FaChevronDown className={`sidebar-hr-caret ${hrMenuOpen ? "open" : ""}`} />
                  )}
                </button>
                {isNavOpen && hrMenuOpen && (
                  <ul className="sidebar-hr-submenu">
                    {hrPaths.map(renderNavLink)}
                  </ul>
                )}
              </li>
            )}
          </ul>
          
          {/* Logout Button at Bottom */}
          <div className="sidebar-logout">
            <button
              onClick={handleLogout}
              className="logout-btn"
              data-tooltip="Logout"
              aria-label="Logout"
            >
              <FaSignOutAlt />
              {isNavOpen && <span>Logout</span>}
            </button>
          </div>
        </nav>
      </aside>
    </div>
  );
};

export default Sidebar;
